import { generateCloudinaryLink } from '../../utils/cloudinary';
import { isGroundClippingEnabled } from './clipping';
import { getMap } from './map';

const byPlaceId = new Map();
const byHandleId = new Map();
const lodConfigured = new Map();
const pendingAdds = new Map();
let hoveredHandleId = null;

function numberOr(value, fallback) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function placedName(place) {
  const name = String(
    place.display_name || place.name || `Place ${place.id}`
  ).trim();
  return name || `Place ${place.id}`;
}

function highlightColorFromPlace(place) {
  if (typeof place?.hover_color !== 'string') {
    return undefined;
  }
  const color = place.hover_color.trim();
  return color || undefined;
}

function lodLevels(lod) {
  if (!lod) {
    return [];
  }
  let parsed = lod;
  if (typeof lod === 'string') {
    try {
      parsed = JSON.parse(lod);
    } catch {
      return [];
    }
  }
  if (!parsed || typeof parsed !== 'object') {
    return [];
  }
  return Object.values(parsed)
    .filter((level) => level && Number.isFinite(Number(level.zoomLvl)))
    .map((level) => ({
      zoomLvl: Number(level.zoomLvl),
      size:
        Number.isFinite(Number(level.size)) && Number(level.size) > 0
          ? Number(level.size)
          : 256,
    }))
    .sort((left, right) => left.zoomLvl - right.zoomLvl);
}

export function resetPlacedModels() {
  byPlaceId.clear();
  byHandleId.clear();
  lodConfigured.clear();
  pendingAdds.clear();
  hoveredHandleId = null;
}

export function getModelById(id) {
  if (id == null) {
    return undefined;
  }
  const key = String(id);
  return byPlaceId.get(key)?.handle || byHandleId.get(key)?.handle;
}

export function hasPlacedModel(placeId) {
  return byPlaceId.has(String(placeId));
}

function remember(placeId, handle, url, options) {
  const record = { handle, placeId: String(placeId), url, options };
  byPlaceId.set(String(placeId), record);
  byHandleId.set(handle.id, record);
  return handle;
}

function forget(placeId) {
  const record = byPlaceId.get(String(placeId));
  if (!record) {
    return;
  }
  byPlaceId.delete(String(placeId));
  byHandleId.delete(record.handle.id);
}

export async function addPlacedModel(place, url, options = {}) {
  const map = getMap();
  if (!map?.models || !place?.id || !url) {
    return null;
  }
  const placeKey = String(place.id);
  const existing = byPlaceId.get(placeKey);
  const generation = (pendingAdds.get(placeKey) || 0) + 1;
  pendingAdds.set(placeKey, generation);
  let handle;
  try {
    const highlightColor = highlightColorFromPlace(place);
    handle = await map.models.add({
      url,
      position: {
        lng: Number(place.longitude),
        lat: Number(place.latitude),
        alt: Number(place.elevation) || 0,
      },
      scale: numberOr(place.scale, 1),
      yawDegrees: Number(place.rotation) || 0,
      name: placedName(place),
      clipGround: isGroundClippingEnabled(place.ground_plane_clipping_enabled),
      doubleSided: Boolean(place.doubleSided),
      pngTransparency: Boolean(options.pngTransparency),
      onClick: () => {
        try {
          options.onClick?.();
        } finally {
          map.models.highlight?.(null);
        }
      },
      ...(highlightColor ? { highlightColor } : {}),
    });
  } catch (error) {
    console.error('Cannot place model:', error);
    return null;
  }
  if (pendingAdds.get(placeKey) !== generation) {
    map.models.remove(handle);
    return null;
  }
  if (existing) {
    map.models.remove(existing.handle);
    forget(place.id);
  }
  return remember(place.id, handle, url, options);
}

export async function replacePlacedModel(place, patch = {}) {
  const record = byPlaceId.get(String(place.id));
  if (!record) {
    return null;
  }
  return addPlacedModel({ ...place, ...patch }, record.url, {
    ...record.options,
    pngTransparency:
      patch.pngTransparency ?? record.options?.pngTransparency,
  });
}

export function setPlacedModelPose(placeId, pose) {
  const record = byPlaceId.get(String(placeId));
  const map = getMap();
  if (!record || !map?.models) {
    return;
  }
  map.models.setPose(record.handle, pose);
}

export function setPlacedModelHighlightColor(placeId, color) {
  const record = byPlaceId.get(String(placeId));
  const map = getMap();
  if (!record || !map?.models?.setHighlightColor) {
    return;
  }
  const next =
    typeof color === 'string' && color.trim() ? color.trim() : null;
  map.models.setHighlightColor(record.handle, next);
}

export function removePlacedModel(placeId) {
  const placeKey = String(placeId);
  pendingAdds.set(placeKey, (pendingAdds.get(placeKey) || 0) + 1);
  const record = byPlaceId.get(placeKey);
  const map = getMap();
  if (!record || !map?.models) {
    return;
  }
  map.models.remove(record.handle);
  forget(placeId);
}

export function hoverModelAtPoint(x, y) {
  const map = getMap();
  if (!map?.models?.pick || !map.models.hover) {
    return false;
  }
  const handle = map.models.pick(x, y);
  const nextId = handle?.id ?? null;
  if (nextId === hoveredHandleId) {
    return nextId != null;
  }
  hoveredHandleId = nextId;
  map.models.hover(handle);
  return nextId != null;
}

export function highlightPlacedModel(placeId, on = true) {
  const map = getMap();
  const handle = getModelById(placeId);
  if (!map?.models) {
    return;
  }
  map.models.highlight(on && handle ? handle : null);
}

export function configureModelLod({
  sourceUrl,
  lod,
  textureFiles,
  mapId,
  modelId,
  isMobile,
}) {
  const map = getMap();
  if (!map?.models || !sourceUrl) {
    return;
  }
  const files = Array.isArray(textureFiles)
    ? textureFiles.filter((fileName) => typeof fileName === 'string')
    : [];
  const levels = lodLevels(lod);
  if (!files.length || !levels.length) {
    return;
  }
  const signature = JSON.stringify({
    levels,
    files,
    isMobile: Boolean(isMobile),
  });
  if (lodConfigured.get(sourceUrl) === signature) {
    return;
  }
  map.models.configureLod({
    sourceUrl,
    levels,
    textureFiles: files,
    isMobile: Boolean(isMobile),
    resolveUrl(fileName, size) {
      return generateCloudinaryLink({
        map: mapId,
        id: modelId,
        fileName,
        params: `w_${size},h_${size},f_auto,q_auto`,
      });
    },
  });
  lodConfigured.set(sourceUrl, signature);
}

export function syncMapLights(lights) {
  const map = getMap();
  if (!map?.models) {
    return;
  }
  const list = Object.values(lights || {}).map((light) => ({
    type: light.light_type || 'hemisphere',
    intensity: numberOr(light.light_intensity, 1),
  }));
  map.models.setLights(list);
}
